/**
 * 导入工具类
 */
import { bridgeCallHandler } from '../../core';
import { copyTextInBrowser, printNativeMethodFallback } from '../../internal';
import { isAndroid, isInApp, isIOS } from '../../utils';

/**
 * 导入类型声明
 */
import type { BridgeResponse, BridgeResponseCallback, harmonyPayOptions, StatusBarOptions } from '../../types';

/**
 * 获取当前设备模式
 * @param callback
 * @returns
 */
export const getSystemTheme = (callback: BridgeResponseCallback): void => {
  const isApp: boolean = isInApp();

  if (!isApp) {
    printNativeMethodFallback('getSystemTheme', 'getSystemTheme');
    return;
  }

  bridgeCallHandler('getSystemTheme', '', callback);
};

/**
 * 拨打电话
 * @param phone
 */
export const callPhone = (phone: string): void => {
  const isApp: boolean = isInApp();

  if (!isApp) {
    printNativeMethodFallback('callPhone', 'callPhone');
    return;
  }

  bridgeCallHandler('callPhone', phone, null);
};

/**
 * 获取状态栏高度
 * @param callback
 * @returns
 */
export const getStatusBarHeight = (callback: BridgeResponseCallback): void => {
  const isApp: boolean = isInApp();

  if (!isApp) {
    printNativeMethodFallback('getStatusBarHeight', 'getStatusBarHeight');
    return;
  }

  if (isIOS()) {
    bridgeCallHandler('getStatusBarHeight', '', (val: BridgeResponse) => {
      if (!val) {
        callback(0);
        return;
      }

      const height: number = Math.floor(Number(val));
      callback(height);
    });
  }
  if (isAndroid()) {
    const element: HTMLDivElement = document.createElement('div');
    element.style.height = '1rem';
    document.body.appendChild(element);
    const statusBarHeight: number = element.getBoundingClientRect().height;
    document.body.removeChild(element);
    callback(statusBarHeight);
  }
};

/**
 * 获取原生主题
 * @returns
 */
export const getNativeTheme = (callback: BridgeResponseCallback): void => {
  const isApp: boolean = isInApp();

  if (!isApp) {
    printNativeMethodFallback('getNativeTheme', 'getNativeTheme');
    return;
  }

  bridgeCallHandler('getNativeTheme', '', callback);
};

/**
 * 禁止左滑关闭事件
 * @param flag
 * @returns
 */
export const disableLeftSwipeClose = (flag: boolean = true): void => {
  const isApp: boolean = isInApp();

  if (!isApp) {
    printNativeMethodFallback('disableLeftSwipeClose', 'disableLeftSwipeClose');
    return;
  }
  const val: string = flag ? 'disabled' : 'enabled';
  bridgeCallHandler('disableLeftSwipeClose', val, null);
};

/**
 * 调用客户端打开录音权限
 * @returns
 */
export const requestRecordPermission = (callback: (data: string) => void): void => {
  const isApp: boolean = isInApp();

  if (!isApp) {
    printNativeMethodFallback('requestRecordPermission', 'requestRecordPermission');
    return;
  }

  bridgeCallHandler('requestRecordPermission', '', callback);
};

/**
 * 禁止息屏
 * @param flag
 */
export const setScreenOnStatus = (flag: boolean): void => {
  const isApp: boolean = isInApp();

  if (!isApp) {
    printNativeMethodFallback('setScreenOnStatus', 'setScreenOnStatus');
    return;
  }
  const val: string = flag ? 'Y' : 'N';
  bridgeCallHandler('setScreenOnStatus', val, null);
};

/**
 * 获取IOS系统版本号
 * @param callback
 * @returns
 */
export const iosSystemVersion = (callback: BridgeResponseCallback): void => {
  const isApp: boolean = isInApp();

  if (!isApp) {
    printNativeMethodFallback('iosSystemVersion', 'iosSystemVersion');
    return;
  }

  bridgeCallHandler('iosSystemVersion', '', callback);
};

/**
 * 触发原生震动反馈
 * @param duration
 * @returns
 */
export const vibrate = (duration: number = 100): void => {
  const isApp: boolean = isInApp();

  if (!isApp) {
    if (navigator.vibrate) {
      navigator.vibrate(duration);
    }
    return;
  }

  bridgeCallHandler('vibrate', duration.toString(), null);
};

/**
 * ios 判断是否需要跳转微信
 * @returns
 */
export const getIsNeedJumpToWechatButtonForIOS = (callback: (data: string) => void): void => {
  const isApp: boolean = isInApp();

  if (!isApp) {
    printNativeMethodFallback('getIsNeedJumpToWechatButtonForIOS', 'getIsNeedJumpToWechatButtonForIOS');
    return;
  }

  bridgeCallHandler('getIsNeedJumpToWechatButtonForIOS', '', callback);
};

/**
 * 复制方法
 * @param data
 * @returns
 */
export const copyText = (text: string): void => {
  const isApp: boolean = isInApp();

  if (isApp) {
    bridgeCallHandler('copyData', text, null);
    return;
  }

  copyTextInBrowser(text);
};

/**
 * 鸿蒙支付
 */
export const harmonyPay = (data: harmonyPayOptions, callback?: (data: any) => {}): void => {
  if (!isInApp()) {
    printNativeMethodFallback('harmonyPay', 'harmonyPay');
    return;
  }
  const params = JSON.stringify(data);
  bridgeCallHandler('harmonyPay', params, callback ? callback : null);
};

/**
 * 设置navbar标题
 */
export const setNavbarTitle = (value: string): void => {
  if (!isInApp()) {
    printNativeMethodFallback('setNavbarTitle', 'setNavbarTitle');
    return;
  }
  bridgeCallHandler('setNavbarTitle', value, null);
};

/**
 * 设置状态栏颜色
 */
export const setStatusBar = (options: StatusBarOptions): void => {
  if (!isInApp()) {
    printNativeMethodFallback('setStatusBar', 'setStatusBar');
    return;
  }
  const params = JSON.stringify(options);
  bridgeCallHandler('setStatusBar', params, null);
};
